import { LogOut, User, ChevronDown, HelpCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { HeaderSearchDropdown } from '@/components/search/HeaderSearchDropdown';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { LiveClock } from '@/components/notifications/LiveClock';
import { GlossaryDialog } from '@/components/glossary/GlossaryDialog';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { startGuidedTour } from '@/components/onboarding/GuidedTour';
import { AiTasksIndicator } from '@/components/ai/AiTasksIndicator';

export function Topbar() {
  const { user, signOut } = useAuth();

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'manager':
        return 'default';
      default:
        return 'secondary';
    }
  };

  return (
    <header className="h-16 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-6 flex items-center justify-between sticky top-0 z-50">
      {/* Inline Search */}
      <HeaderSearchDropdown />

      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* AI Tasks Indicator */}
        <AiTasksIndicator />
        
        {/* Theme Toggle */}
        <ThemeToggle />
        
        {/* Glossary */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <GlossaryDialog />
            </div>
          </TooltipTrigger>
          <TooltipContent>Glossaire CEE</TooltipContent>
        </Tooltip>
        
        {/* Live Clock */}
        <LiveClock />
        
        {/* Separator */}
        <div className="hidden md:block h-8 w-px bg-border" />
        
        {/* Notification Bell */}
        <div data-tour="notifications">
          <NotificationBell />
        </div>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="flex items-center gap-2 px-3">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div className="hidden md:flex flex-col items-start">
                <span className="text-sm font-medium">{user?.name}</span>
                <Badge variant={getRoleBadgeVariant(user?.role || '')} className="text-xs h-5">
                  {user?.role}
                </Badge>
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span>{user?.name}</span>
                <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => startGuidedTour()}>
              <HelpCircle className="mr-2 h-4 w-4" />
              Relancer le tour guidé
            </DropdownMenuItem>
            <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              Déconnexion
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
